import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Autocomplete,
  FormHelperText,
  CircularProgress,
  useTheme,
  useMediaQuery,
  InputAdornment,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useFormik } from "formik";
import * as Yup from "yup";

import { Field, Step2Values } from "../types";
import eligibilityCheckServices from "../../../services/api/service/eligibilityCheckService";
import dropdownService from "../../../services/api/service/dropdownService";
import { topCountry } from "../../../data/stepsData";
import RightPanel from "../components/RightPanel";
import { useDispatch, useSelector } from "react-redux";
import {
  setCourseDetails,
  DropdownOptionValue,
  Step2FormValues,
} from "../../../services/redux/slices/newJourneyEligibilitycheckSlice";
import MultiStarIcon from "../../../assets/icons/multi_star.png";
import Step2CourseDiscoverySkeleton from "../skeletons/Step2CourseDiscoverySkeleton";
import { setEncryptionKey } from "../../../services/redux/slices/newJourneyEligibilitycheckSlice";
import losServices from "../../../services/api/service/losService";
import { getSanitizedPublicKeyFromResponse } from "../../../utils/piiKey";
import { encryptData } from "../../../utils/crypto";
import { setStatusPopup } from "../../../services/redux/slices/statusPopupSlice";
import StickyFooter from "../components/StickyFooter";
import Sidebar from "../components/Sidebar";

type Step2FormFieldName =
  | "courseCategory"
  | "admissionStatus"
  | "country"
  | "university"
  | "institute"
  | "courseName"
  | "courseType";

type Step2FormFields = Record<Step2FormFieldName, string>;

interface DropdownOption {
  value: string;
  label: string;
  code?: string;
}

interface DropdownData {
  countries: DropdownOption[];
  institutes: DropdownOption[];
  universities: DropdownOption[];
  courses: DropdownOption[];
  courseCategory: DropdownOption[];
  admissionStatus: DropdownOption[];
  courseType: DropdownOption[];
}

interface NotInListState {
  university: boolean;
  institute: boolean;
  courseName: boolean;
}

interface LoadingDropdowns {
  universities: boolean;
  institutes: boolean;
  courses: boolean;
}

interface Step2Props {
  fields: Field[];
  initialValues: Partial<any>;
  onNext: (values: Step2Values) => void;
  onBack: () => void;
  currentStep: number;
  progress: number;
  completedSteps: any;
}

const buildValidationSchema = (
  fields: Field[],
  admissionStatus?: string,
): Yup.ObjectSchema<Step2FormFields> => {
  const VISIBLE_TYPES = new Set(["dropdown", "autocomplete"]);
  const isUndecided = admissionStatus === "Undecided";

  const shape: Record<string, Yup.Schema> = {
    courseCategory: Yup.string().required("Please select a course category."),
    admissionStatus: Yup.string().required(
      "Please select an admission status.",
    ),
    country: Yup.string().required("Please select a Study Destination."),
    // When Undecided — university, institute, courseName become optional
    university: isUndecided
      ? Yup.string().notRequired()
      : Yup.string().required("Please select a University Name."),
    institute: isUndecided
      ? Yup.string().notRequired()
      : Yup.string().required("Please select an Institute Name."),
    courseName: isUndecided
      ? Yup.string().notRequired()
      : Yup.string().required("Please select a Course Name."),
    courseType: Yup.string().required("Please select a Course Type."),
  };

  fields.forEach((f) => {
    if (VISIBLE_TYPES.has(f.type) && !f.required && f.name in shape) {
      shape[f.name] = Yup.string().notRequired();
    }
  });

  return Yup.object(shape) as Yup.ObjectSchema<Step2FormFields>;
};

const isContinueDisabled = (
  values: Step2FormFields,
  fields: Field[],
  isValid: boolean,
): boolean => {
  if (!isValid) return true;

  const isUndecided = values.admissionStatus === "Undecided";

  const OPTIONAL_WHEN_UNDECIDED = new Set([
    "university",
    "institute",
    "courseName",
  ]);

  const requiredVisibleNames = fields
    .filter(
      (f) =>
        !f.isRemoved &&
        f.required &&
        f.type !== "hidden" &&
        f.type !== "boolean" &&
        !(isUndecided && OPTIONAL_WHEN_UNDECIDED.has(f.name)),
    )
    .map((f) => f.name);

  return requiredVisibleNames.some(
    (name) => !values[name as Step2FormFieldName]?.toString().trim(),
  );
};

interface SelectFieldProps {
  name: Step2FormFieldName;
  label: string;
  options: DropdownOption[];
  value: string;
  error: boolean;
  helperText: string | undefined;
  size: "small" | "medium";
  loading: boolean;
  onChange: (e: SelectChangeEvent<string>) => void;
  onBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  isMobile: boolean;
}

const CustomArrowIcon = (props: React.ComponentProps<typeof KeyboardArrowDownIcon>) => (
  <KeyboardArrowDownIcon
    {...props}
    sx={{ fontSize: 20, color: "#3D4948 !important" }}
  />
);

const SelectField: React.FC<SelectFieldProps> = ({
  name,
  label,
  options,
  value,
  error,
  helperText,
  size,
  loading,
  onChange,
  onBlur,
  isMobile
}) => (
  <Box data-field-name={name}>
    {/* Label above the field */}
    <Typography
      sx={{
        fontSize: "14px",
        fontWeight: 600,
        color: "#3D4948",
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    <FormControl fullWidth size={size} error={error}>
      <Select<string>
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Typography sx={{ color: "#aaa", fontSize: "0.9rem" }}>
                Select
              </Typography>
            );
          }
          return options.find((o) => o.value === selected)?.label ?? selected;
        }}
        IconComponent={CustomArrowIcon}
        sx={{
          borderRadius: "12px",
          bgcolor: "#F0F2F5 !important",
          padding: isMobile ? "4px 9px !important" : "2px 9px !important",
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
          "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            border: "1.5px solid",
            borderColor: "error.main",
          },
        }}
        endAdornment={
          loading ? (
            <CircularProgress
              size={16}
              sx={{ mr: 3, color: "text.secondary" }}
            />
          ) : undefined
        }
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      {error && helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  </Box>
);

interface AutocompleteFieldProps {
  name: Step2FormFieldName;
  label: string;
  options: DropdownOption[];
  value: string;
  error: boolean;
  helperText: string | undefined;
  size: "small" | "medium";
  loading: boolean;
  onValueChange: (
    name: Step2FormFieldName,
    value: string,
    option?: DropdownOption | null,
  ) => void;
  onBlur: (name: Step2FormFieldName) => void;
  onInputChange?: (value: string) => void;
  notFoundLabel: string;
  onNotFoundClick: () => void;
  displayValue?: string;
  isUndecided: boolean;
}

const AutocompleteField: React.FC<AutocompleteFieldProps> = ({
  name,
  label,
  options,
  value,
  error,
  helperText,
  size,
  loading,
  onValueChange,
  onBlur,
  onInputChange,
  notFoundLabel,
  onNotFoundClick,
  displayValue,
  isUndecided
}) => {
  const resolvedValue =
    options.find((o) => o.value === value) ??
    (value ? { value, label: displayValue || value } : null);

  return (
    <Box data-field-name={name}>
      {/* Label row with "Not found?" link */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.75,
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            color: "#3D4948",
            fontWeight: 600,
          }}
        >
          {label} {isUndecided ? "" : "*"}
        </Typography>
        <Typography
          onClick={onNotFoundClick}
          sx={{
            fontSize: "0.78rem",
            color: "primary.main",
            fontWeight: 600,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {notFoundLabel}
        </Typography>
      </Box>

      <Autocomplete
        fullWidth
        freeSolo
        loading={loading}
        options={options}
        getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.label)}
        isOptionEqualToValue={(opt, val) =>
          typeof val === "string" ? opt.value === val : opt.value === val.value
        }
        value={resolvedValue}
        onChange={(_, selected) => {
          if (!selected) {
            onValueChange(name, "", null);
          } else if (typeof selected === "string") {
            onValueChange(name, selected, null);
          } else {
            onValueChange(name, selected.value, selected);
          }
        }}
        onInputChange={(_, inputVal, reason) => {
          if (reason === "input" && onInputChange) {
            onInputChange(inputVal);
          }
        }}
        onBlur={() => onBlur(name)}
        filterOptions={(x) => x}
        noOptionsText={loading ? "Loading…" : "Type 3 letters to search"}
        popupIcon={null}
        renderInput={(params) => (
          <TextField
            {...params}
            size={size}
            error={error}
            helperText={error ? helperText : undefined}
            placeholder={`Type 3 Letters of ${label}`}
            InputProps={{
              ...params.InputProps,
              // Search icon on the left
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: "#3D4948" }} />
                </InputAdornment>
              ),
              // Spinner (when loading) + dropdown arrow on the right
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null}
                  <InputAdornment position="end">
                    <KeyboardArrowDownIcon
                      sx={{ fontSize: 20, color: "#3D4948" }}
                    />
                  </InputAdornment>
                </>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                bgcolor: "#F0F2F5",
                padding: {xs: "11px 9px !important", sm: "13px 9px"},
                "& fieldset": { border: "none" },
                "&:hover fieldset": { border: "none" },
                "&.Mui-focused fieldset": { border: "none" },
                "&.Mui-error fieldset": {
                  border: "1.5px solid",
                  borderColor: "error.main",
                },
              },
            }}
          />
        )}
      />
    </Box>
  );
};

interface FreeTextFieldProps {
  name: Step2FormFieldName;
  label: string;
  value: string;
  error: boolean;
  helperText: string | undefined;
  size: "small" | "medium";
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (name: Step2FormFieldName) => void;
  onInListClick: () => void;
}

const FreeTextField: React.FC<FreeTextFieldProps> = ({
  name,
  label,
  value,
  error,
  helperText,
  size,
  onChange,
  onBlur,
  onInListClick,
}) => (
  <Box data-field-name={name}>
    {/* Label row with "In list" link */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 0.75,
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          color: error ? "error.main" : "#3D4948",
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Typography
        onClick={onInListClick}
        sx={{
          fontSize: "0.78rem",
          color: "primary.main",
          fontWeight: 600,
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        In list
      </Typography>
    </Box>

    <TextField
      fullWidth
      size={size}
      name={name}
      value={value}
      onChange={onChange}
      onBlur={() => onBlur(name)}
      error={error}
      helperText={error ? helperText : undefined}
      placeholder={`Enter ${label}`}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "12px",
          bgcolor: "#F0F2F5",
          padding: "4.3px 9px",
          "& fieldset": { border: "none" },
          "&:hover fieldset": { border: "none" },
          "&.Mui-focused fieldset": { border: "none" },
          "&.Mui-error fieldset": {
            border: "1.5px solid",
            borderColor: "error.main",
          },
        },
      }}
    />
  </Box>
);

const Step2CourseDiscovery: React.FC<Step2Props> = ({
  fields,
  initialValues,
  onNext,
  onBack,
  currentStep,
  progress,
  completedSteps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const fieldSize: "small" | "medium" = isMobile ? "small" : "medium";

  const dispatch = useDispatch();

  const formRef = useRef<HTMLDivElement>(null);

  const encryptionKey = useSelector(
    (state: any) => state.eligibilityCheck.encryptionKey,
  );

  const mobileNumber = useSelector(
    (state: any) =>
      state?.eligibilityCheck?.stepFields?.basicDetails?.phone_number,
  );

  const extractValue = (field: any): string => {
    if (!field) return "";
    if (typeof field === "string") return field;
    if (typeof field === "object" && field.value) return field.value;
    return "";
  };

  const extractLabel = (field: any): string => {
    if (!field) return "";
    if (typeof field === "object" && field.label) return field.label;
    return "";
  };

  const extractCode = (field: any): string => {
    if (!field) return "";
    if (typeof field === "object" && field.code) return field.code;
    return "";
  };

  const [labelMap, setLabelMap] = useState<Record<string, string>>({
    country:
      initialValues?.countryLabel || extractLabel(initialValues?.country) || "",
    university:
      initialValues?.universityLabel ||
      extractLabel(initialValues?.university) ||
      "",
    institute: initialValues?.instituteNotInList
      ? "" // free-text mode — no label needed
      : initialValues?.instituteLabel ||
        extractLabel(initialValues?.institute) ||
        "",
    courseName: initialValues?.courseNameLabel || "",
  });

  const [notInList, setNotInList] = useState<NotInListState>({
    university:
      initialValues?.notInListUniversity ??
      initialValues?.universityNotInList ??
      false,
    institute:
      initialValues?.notInListInstitute ??
      initialValues?.instituteNotInList ??
      false,
    courseName:
      initialValues?.notInListCourseName ??
      initialValues?.courseNameNotInList ??
      false,
  });

  const [selectedCountryId, setSelectedCountryId] = useState<string>(
    initialValues?.selectedCountryId ||
      extractValue(initialValues?.country) ||
      initialValues?.universityCountryId ||
      "",
  );
  const [selectedUniversityId, setSelectedUniversityId] = useState<string>(
    initialValues?.selectedUniversityId ||
      extractValue(initialValues?.university) ||
      "",
  );
  const [selectedInstituteId, setSelectedInstituteId] = useState<string>(
    initialValues?.selectedInstituteId ||
      (initialValues?.notInListInstitute
        ? ""
        : extractValue(initialValues?.institute)) ||
      "",
  );

  const [selectedUniversityCode, setSelectedUniversityCode] = useState<string>(
    extractCode(initialValues?.university) || "",
  );
  const [selectedInstituteCode, setSelectedInstituteCode] = useState<string>(
    extractCode(initialValues?.institute) || "",
  );
  const [selectedCourseCode, setSelectedCourseCode] = useState<string>(
    extractCode(initialValues?.courseName) || "",
  );

  // ── loading states ────────────────────────────────────────────────────────

  const [masterLoading, setMasterLoading] = useState(false);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [loadingDropdowns, setLoadingDropdowns] = useState<LoadingDropdowns>({
    universities: false,
    institutes: false,
    courses: false,
  });

  // ── dropdown data ─────────────────────────────────────────────────────────

  const [dropdownData, setDropdownData] = useState<DropdownData>({
    countries: [],
    institutes: [],
    universities: [],
    courses: [],
    courseCategory: [],
    admissionStatus: [],
    courseType: [],
  });
  const [isBtnLoading, setIsBtnLoading] = useState(false);

  const uniDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const instDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const courseDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchMasterData = useCallback(async (): Promise<void> => {
    setMasterLoading(true);
    try {
      const res = await eligibilityCheckServices.getMasters();
      if (res.data?.status === "success") {
        const {
          categories = [],
          statusesOfAdmission = [],
          courseTypes = [],
        } = res.data.data;
        setDropdownData((prev) => ({
          ...prev,
          courseCategory: categories.map(
            (cat: { categoryCode: string; categoryName: string }) => ({
              value: cat.categoryCode,
              label: cat.categoryName,
            }),
          ),
          admissionStatus: statusesOfAdmission.map(
            (stat: { statusName: string }) => ({
              value: stat.statusName,
              label: stat.statusName,
            }),
          ),
          courseType: courseTypes.map(
            (type: { typeCode: string; typeName: string }) => ({
              value: type.typeCode,
              label: type.typeName,
            }),
          ),
        }));
      }
    } catch (error) {
      console.error("Failed to fetch master data", error);
    } finally {
      setMasterLoading(false);
    }
  }, []);

  const fetchCountries = useCallback(async (): Promise<void> => {
    setCountriesLoading(true);
    try {
      const res = await dropdownService.getCountries();

      const allCountries: DropdownOption[] = res.data.map(
        (c: { countryId: string; countryDesc: string }) => ({
          value: c.countryId,
          label: c.countryDesc,
        }),
      );

      const topCountries: DropdownOption[] = [];
      const restCountries: DropdownOption[] = [];

      allCountries.forEach((country) => {
        const upper = country.label.toUpperCase();
        const isTop = topCountry.some((name: string) => upper.includes(name));
        if (isTop) topCountries.push(country);
        else restCountries.push(country);
      });

      topCountries.sort((a, b) => {
        const aIdx = topCountry.findIndex((name: string) =>
          a.label.toUpperCase().includes(name),
        );
        const bIdx = topCountry.findIndex((name: string) =>
          b.label.toUpperCase().includes(name),
        );
        return aIdx - bIdx;
      });

      restCountries.sort((a, b) => a.label.localeCompare(b.label));

      setDropdownData((prev) => ({
        ...prev,
        countries: [...topCountries, ...restCountries],
      }));
    } catch (error) {
      console.error("Error fetching countries", error);
    } finally {
      setCountriesLoading(false);
    }
  }, []);

  const fetchUniversities = useCallback(
    async (searchValue: string): Promise<void> => {
      if (!selectedCountryId) {
        setDropdownData((prev) => ({ ...prev, universities: [] }));
        return;
      }
      if (searchValue.length < 3) return;

      setLoadingDropdowns((prev) => ({ ...prev, universities: true }));
      try {
        const res = await dropdownService.getUniversities({
          universityName: searchValue,
          countryId: selectedCountryId,
        });

        const options: DropdownOption[] = res.data
          .filter((u: any) => u.active)
          .map((u: any) => ({
            value: u.universityId.toString(),
            label: u.universityName,
            code: u.universityCode.toString(),
          }));

        setDropdownData((prev) => ({ ...prev, universities: options }));
      } catch (error) {
        console.error("Error fetching universities:", error);
      } finally {
        setLoadingDropdowns((prev) => ({ ...prev, universities: false }));
      }
    },
    [selectedCountryId],
  );

  const fetchInstitutes = useCallback(
    async (searchValue: string): Promise<void> => {
      if (!selectedCountryId || !selectedUniversityId) {
        setDropdownData((prev) => ({ ...prev, institutes: [] }));
        return;
      }
      if (searchValue.length < 3) return;

      setLoadingDropdowns((prev) => ({ ...prev, institutes: true }));
      try {
        const res = await dropdownService.getInstitutes({
          instituteName: searchValue,
          universityId: selectedUniversityId,
          countryId: selectedCountryId,
        });

        const options: DropdownOption[] = res.data
          .filter((i: any) => i.active)
          .map((i: any) => ({
            value: i.instituteId.toString(),
            label: i.instituteName,
            code: i.instituteCode.toString(),
          }));

        setDropdownData((prev) => ({ ...prev, institutes: options }));
      } catch (error) {
        console.error("Error fetching institutes:", error);
      } finally {
        setLoadingDropdowns((prev) => ({ ...prev, institutes: false }));
      }
    },
    [selectedCountryId, selectedUniversityId],
  );

  const fetchCourses = useCallback(
    async (searchValue: string): Promise<void> => {
      if (!selectedInstituteId) {
        setDropdownData((prev) => ({ ...prev, courses: [] }));
        return;
      }
      if (searchValue.length < 3) return;

      setLoadingDropdowns((prev) => ({ ...prev, courses: true }));
      try {
        const res = await dropdownService.getCourses({
          courseName: searchValue,
          instituteId: selectedInstituteId,
        });

        const options: DropdownOption[] = res.data
          .filter((c: any) => c.recordStatus === "Approved")
          .map((c: any) => ({
            value: c.courseId.toString(),
            label: c.courseName,
            code: c.courseCode.toString(),
          }));

        setDropdownData((prev) => ({ ...prev, courses: options }));
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setLoadingDropdowns((prev) => ({ ...prev, courses: false }));
      }
    },
    [selectedInstituteId],
  );

  useEffect(() => {
    void fetchMasterData();
    void fetchCountries();
  }, [fetchMasterData, fetchCountries]);

  const [validationSchema, setValidationSchema] = useState(() =>
    buildValidationSchema(fields, initialValues?.admissionStatus ?? ""),
  );

  const visibleFields = useMemo<Field[]>(
    () =>
      fields
        .filter((f) => !f.isRemoved && f.type !== "hidden")
        .sort((a, b) => a.fieldOrder - b.fieldOrder),
    [fields],
  );

  const hiddenFields = useMemo<Field[]>(
    () => fields.filter((f) => !f.isRemoved && f.type === "hidden"),
    [fields],
  );

  const formik = useFormik<Step2FormFields>({
    initialValues: {
      courseCategory: initialValues?.courseCategory ?? "",
      admissionStatus: initialValues?.admissionStatus ?? "",
      country: extractValue(initialValues?.country) || "",
      university: initialValues?.notInListUniversity
        ? typeof initialValues?.university === "string"
          ? initialValues.university
          : ""
        : extractValue(initialValues?.university) || "",
      institute: initialValues?.notInListInstitute
        ? typeof initialValues?.institute === "string"
          ? initialValues.institute
          : ""
        : extractValue(initialValues?.institute) || "",

      courseName: initialValues?.notInListCourseName
        ? typeof initialValues?.courseName === "string"
          ? initialValues.courseName
          : ""
        : extractValue(initialValues?.courseName) || "",

      courseType: initialValues?.courseType ?? "",
    },
    validationSchema,
    validateOnMount: true,
    enableReinitialize: false,
    onSubmit: (values) => {
      const countryPayload: DropdownOptionValue | string = labelMap.country
        ? { value: values.country, label: labelMap.country }
        : values.country;

      const universityPayload: DropdownOptionValue | string =
        notInList.university
          ? values.university // free text — plain string
          : labelMap.university
            ? {
                value: values.university, // universityId — for child API calls
                label: labelMap.university,
                code: selectedUniversityCode, // universityCode — for captureFormData/BRE
              }
            : values.university;

      const institutePayload: DropdownOptionValue | string = notInList.institute
        ? values.institute // free text — plain string
        : labelMap.institute
          ? {
              value: values.institute, // instituteId — for child API calls
              label: labelMap.institute,
              code: selectedInstituteCode, // instituteCode — for captureFormData/BRE
            }
          : values.institute;

      // courseName: store with code so captureFormData can extract courseCode
      const courseNamePayload: DropdownOptionValue | string =
        notInList.courseName
          ? values.courseName // free text — plain string
          : labelMap.courseName
            ? {
                value: values.courseName, // courseId
                label: labelMap.courseName,
                code: selectedCourseCode, // courseCode — for captureFormData/BRE
              }
            : values.courseName;

      const courseDetailsPayload: Step2FormValues = {
        // Core form fields
        courseCategory: values.courseCategory,
        admissionStatus: values.admissionStatus,
        country: countryPayload,
        universityCountryId: selectedCountryId,
        universityCampusId: selectedUniversityCode, // code, not ID
        university: universityPayload,
        universityNotInList: notInList.university,
        institute_code: selectedInstituteCode || null, // code, not ID
        institute: institutePayload,
        instituteNotInList: notInList.institute,
        courseCode: selectedCourseCode || null, // course code
        courseName: notInList.courseName
          ? values.courseName
          : (courseNamePayload as any),
        courseNameNotInList: notInList.courseName,
        courseType: values.courseType,
        countryLabel: labelMap.country ?? "",
        universityLabel: labelMap.university ?? "",
        instituteLabel: notInList.institute ? "" : (labelMap.institute ?? ""),
        courseNameLabel: labelMap.courseName ?? "",
        selectedCountryId,
        selectedUniversityId,
        selectedInstituteId,
        notInListUniversity: notInList.university,
        notInListInstitute: notInList.institute,
        notInListCourseName: notInList.courseName,
      };
      desputeCheck(courseDetailsPayload);
    },
  });

  useEffect(() => {
    // Defer to next tick so the schema state is flushed before validating
    const t = setTimeout(() => {
      void formik.validateForm();
    }, 0);
    return () => clearTimeout(t);
  }, []); // run once on mount only

  useEffect(() => {
    const newSchema = buildValidationSchema(
      fields,
      formik.values.admissionStatus,
    );
    setValidationSchema(newSchema);
    const t = setTimeout(() => {
      void formik.validateForm();
    }, 0);
    return () => clearTimeout(t);
  }, [fields, formik.values.admissionStatus]);

  const fetchSanitizedPiiPublicKey = async () => {
    if (encryptionKey) {
      return getSanitizedPublicKeyFromResponse(encryptionKey);
    }
    const piiKeyResponse: any = await losServices.getPiiKey();
    if (piiKeyResponse?.status !== 200) return "";

    const rawKey = piiKeyResponse?.data?.p_k;
    const rawKeyStr = typeof rawKey === "string" ? rawKey : "";
    dispatch(setEncryptionKey(rawKeyStr));

    return getSanitizedPublicKeyFromResponse(rawKey);
  };

  const desputeCheck = async (courseDetailsPayload: any) => {
    // When admissionStatus is "Undecided" — skip dedupeCheck and proceed directly
    if (formik.values.admissionStatus === "Undecided") {
      onNext(courseDetailsPayload as any);
      return;
    }

    setIsBtnLoading(true);
    try {
      const piiPublicKey = await fetchSanitizedPiiPublicKey();
      if (!piiPublicKey) {
        dispatch(
          setStatusPopup({
            status: "error",
            message: "Unable to secure your details. Please try again.",
          }),
        );
        return;
      }

      const payload = await encryptData(
        {
          mobileNumber: mobileNumber || "",
          courseName: formik.values.courseName || labelMap?.courseName || "",
        },
        ["mobileNumber"],
        piiPublicKey,
      );
      await eligibilityCheckServices.dedupeCheck(payload);
      onNext(courseDetailsPayload as any);
    } catch (err: any) {
      console.error("Dedupe check failed", err);
      dispatch(
        setStatusPopup({
          status: "error",
          message: err?.response?.data?.message || "Dedupe check failed",
        }),
      );
      return err;
    } finally {
      setIsBtnLoading(false);
    }
  };

  const clearUniversityAndBelow = useCallback((): void => {
    void formik.setFieldValue("university", "");
    void formik.setFieldValue("institute", "");
    void formik.setFieldValue("courseName", "");
    setSelectedUniversityId("");
    setSelectedUniversityCode("");
    setSelectedInstituteId("");
    setSelectedInstituteCode("");
    setSelectedCourseCode("");
    setDropdownData((prev) => ({
      ...prev,
      universities: [],
      institutes: [],
      courses: [],
    }));
    setNotInList({ university: false, institute: false, courseName: false });
  }, [formik]);

  const clearInstituteAndBelow = useCallback((): void => {
    void formik.setFieldValue("institute", "");
    void formik.setFieldValue("courseName", "");
    setSelectedInstituteId("");
    setSelectedInstituteCode("");
    setSelectedCourseCode("");
    setDropdownData((prev) => ({ ...prev, institutes: [], courses: [] }));
    setNotInList((prev) => ({ ...prev, institute: false, courseName: false }));
  }, [formik]);

  const clearCourseOnly = useCallback((): void => {
    void formik.setFieldValue("courseName", "");
    setSelectedCourseCode("");
    setDropdownData((prev) => ({ ...prev, courses: [] }));
    setNotInList((prev) => ({ ...prev, courseName: false }));
  }, [formik]);

  // ── stable callbacks ──────────────────────────────────────────────────────

  const handleSelectChange = useCallback(
    (e: SelectChangeEvent<string>): void => {
      const name = e.target.name as Step2FormFieldName;
      void formik.setFieldValue(name, e.target.value);
    },
    [formik],
  );

  const handleAutocompleteChange = useCallback(
    (
      name: Step2FormFieldName,
      value: string,
      option?: DropdownOption | null,
    ): void => {
      void formik.setFieldValue(name, value);

      // Always update the label so display is correct when options list is empty
      const label = option?.label ?? "";
      setLabelMap((prev) => ({ ...prev, [name]: label }));

      if (name === "country") {
        const countryId = option?.value ?? value;
        setSelectedCountryId(countryId);
        clearUniversityAndBelow();
        setLabelMap((prev) => ({
          ...prev,
          university: "",
          institute: "",
          courseName: "",
        }));
      } else if (name === "university") {
        const uniId = option?.value ?? "";
        setSelectedUniversityId(uniId);
        setSelectedUniversityCode(option?.code ?? "");
        clearInstituteAndBelow();
        setLabelMap((prev) => ({ ...prev, institute: "", courseName: "" }));
      } else if (name === "institute") {
        const instId = option?.value ?? "";
        setSelectedInstituteId(instId);
        setSelectedInstituteCode(option?.code ?? "");
        clearCourseOnly();
        setLabelMap((prev) => ({ ...prev, courseName: "" }));
      } else if (name === "courseName") {
        setSelectedCourseCode(option?.code ?? "");
      }
    },
    [formik, clearUniversityAndBelow, clearInstituteAndBelow, clearCourseOnly],
  );

  const handleAutocompleteBlur = useCallback(
    (name: Step2FormFieldName): void => {
      void formik.setFieldTouched(name, true, true);
    },
    [formik],
  );

  // ── search-as-you-type handlers (debounced, min 3 chars) ─────────────────

  const handleUniversitySearch = useCallback(
    (inputVal: string): void => {
      if (uniDebounce.current) clearTimeout(uniDebounce.current);
      uniDebounce.current = setTimeout(() => {
        void fetchUniversities(inputVal);
      }, 400);
    },
    [fetchUniversities],
  );

  const handleInstituteSearch = useCallback(
    (inputVal: string): void => {
      if (instDebounce.current) clearTimeout(instDebounce.current);
      instDebounce.current = setTimeout(() => {
        void fetchInstitutes(inputVal);
      }, 400);
    },
    [fetchInstitutes],
  );

  const handleCourseSearch = useCallback(
    (inputVal: string): void => {
      if (courseDebounce.current) clearTimeout(courseDebounce.current);
      courseDebounce.current = setTimeout(() => {
        void fetchCourses(inputVal);
      }, 400);
    },
    [fetchCourses],
  );

  const handleNotFoundClick = useCallback(
    (field: keyof NotInListState): void => {
      if (field === "university") {
        void formik.setFieldValue("university", "");
        void formik.setFieldValue("institute", "");
        void formik.setFieldValue("courseName", "");
        setSelectedUniversityId("");
        setSelectedInstituteId("");
        setDropdownData((prev) => ({
          ...prev,
          universities: [],
          institutes: [],
          courses: [],
        }));
        setNotInList({ university: true, institute: true, courseName: true });
        setLabelMap((prev) => ({
          ...prev,
          university: "",
          institute: "",
          courseName: "",
        }));
      } else if (field === "institute") {
        void formik.setFieldValue("institute", "");
        void formik.setFieldValue("courseName", "");
        setSelectedInstituteId("");
        setDropdownData((prev) => ({ ...prev, institutes: [], courses: [] }));
        setNotInList((prev) => ({
          ...prev,
          institute: true,
          courseName: true,
        }));
        setLabelMap((prev) => ({ ...prev, institute: "", courseName: "" }));
      } else {
        void formik.setFieldValue("courseName", "");
        setDropdownData((prev) => ({ ...prev, courses: [] }));
        setNotInList((prev) => ({ ...prev, courseName: true }));
        setLabelMap((prev) => ({ ...prev, courseName: "" }));
      }
    },
    [formik],
  );

  const handleInListClick = useCallback(
    (field: keyof NotInListState): void => {
      void formik.setFieldValue(
        field === "courseName" ? "courseName" : field,
        "",
      );
      setNotInList((prev) => ({ ...prev, [field]: false }));

      if (field === "university") {
        clearInstituteAndBelow();
        setNotInList({
          university: false,
          institute: false,
          courseName: false,
        });
        setLabelMap((prev) => ({
          ...prev,
          university: "",
          institute: "",
          courseName: "",
        }));
      } else if (field === "institute") {
        clearCourseOnly();
        setNotInList((prev) => ({
          ...prev,
          institute: false,
          courseName: false,
        }));
        setLabelMap((prev) => ({ ...prev, institute: "", courseName: "" }));
      } else {
        setLabelMap((prev) => ({ ...prev, courseName: "" }));
      }
    },
    [formik, clearInstituteAndBelow, clearCourseOnly],
  );

  // ── error helpers ─────────────────────────────────────────────────────────

  const isFieldError = (name: Step2FormFieldName): boolean =>
    Boolean(formik.touched[name] && formik.errors[name]);

  const getFieldError = (name: Step2FormFieldName): string | undefined =>
    formik.touched[name] ? formik.errors[name] : undefined;

  // ── options resolver ──────────────────────────────────────────────────────

  const getSelectOptions = (name: Step2FormFieldName): DropdownOption[] => {
    const map: Partial<Record<Step2FormFieldName, DropdownOption[]>> = {
      courseCategory: dropdownData.courseCategory,
      admissionStatus: dropdownData.admissionStatus,
      courseType: dropdownData.courseType,
    };
    return map[name] ?? [];
  };

  const getAutocompleteOptions = (
    name: Step2FormFieldName,
  ): DropdownOption[] => {
    const map: Partial<Record<Step2FormFieldName, DropdownOption[]>> = {
      country: dropdownData.countries,
      university: dropdownData.universities,
      institute: dropdownData.institutes,
      courseName: dropdownData.courses,
    };
    return map[name] ?? [];
  };

  // ── loading flag per field ────────────────────────────────────────────────

  const isFieldLoading = (name: Step2FormFieldName): boolean => {
    if (
      name === "courseCategory" ||
      name === "admissionStatus" ||
      name === "courseType"
    )
      return masterLoading;
    if (name === "country") return countriesLoading;
    if (name === "university") return loadingDropdowns.universities;
    if (name === "institute") return loadingDropdowns.institutes;
    if (name === "courseName") return loadingDropdowns.courses;
    return false;
  };

  const schemaIsValid = useMemo(() => {
    try {
      validationSchema.validateSync(formik.values, { abortEarly: true });
      return true;
    } catch {
      return false;
    }
  }, [validationSchema, formik.values]);

  const continueDisabled = isContinueDisabled(
    formik.values,
    fields,
    schemaIsValid,
  );

  // ── Scroll to first error field in document order ─────────────────────────
  const scrollToFirstError = useCallback((): void => {
    if (!formRef.current) return;

    setTimeout(() => {
      const container = formRef.current;
      if (!container) return;

      // Priority order for Step 2 fields (AC3)
      const priorityOrder: Step2FormFieldName[] = [
        "courseCategory",
        "admissionStatus",
        "country",
        "university",
        "institute",
        "courseName",
        "courseType",
      ];

      const allFieldEls = Array.from(
        container.querySelectorAll("[data-field-name]"),
      ) as HTMLElement[];

      for (const fieldName of priorityOrder) {
        const el = allFieldEls.find(
          (e) => e.getAttribute("data-field-name") === fieldName,
        );
        if (!el) continue;

        const hasError =
          el.querySelector(".Mui-error") !== null ||
          el.querySelector(".MuiFormHelperText-root.Mui-error") !== null ||
          el.querySelector("[aria-invalid='true']") !== null;

        if (hasError) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          const input = el.querySelector(
            "input, [tabindex]",
          ) as HTMLElement | null;
          input?.focus();
          return;
        }
      }
    }, 100);
  }, []);

  // ── Continue click: touch all → scroll to first error ─────────────────────
  const handleContinueClick = useCallback((): void => {
    const touchedAll = Object.keys(formik.values).reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {} as Record<string, boolean>,
    );

    formik.setTouched(touchedAll, true).then(() => {
      scrollToFirstError();
    });

    void formik.submitForm();
  }, [formik, scrollToFirstError]);

  // ── field renderer ────────────────────────────────────────────────────────

  const renderField = (field: Field): React.ReactNode => {
    const name = field.name as Step2FormFieldName;

    // ── dropdown (MUI Select) ──
    if (field.type === "dropdown") {
      return (
        <SelectField
          name={name}
          label={field.label}
          options={getSelectOptions(name)}
          value={formik.values[name]}
          error={isFieldError(name)}
          helperText={getFieldError(name)}
          size={fieldSize}
          loading={isFieldLoading(name)}
          onChange={handleSelectChange}
          onBlur={formik.handleBlur}
          isMobile={isMobile}
        />
      );
    }

    // ── autocomplete — dependent fields with Not found toggle ──
    if (field.type === "autocomplete") {
      const isNotInListField =
        name === "university" || name === "institute" || name === "courseName";

      if (isNotInListField && notInList[name as keyof NotInListState]) {
        // ── Free-text mode ──
        return (
          <FreeTextField
            name={name}
            label={field.label}
            value={formik.values[name]}
            error={isFieldError(name)}
            helperText={getFieldError(name)}
            size={fieldSize}
            onChange={(e) => {
              void formik.setFieldValue(name, e.target.value);
            }}
            onBlur={handleAutocompleteBlur}
            onInListClick={() =>
              handleInListClick(name as keyof NotInListState)
            }
          />
        );
      }

      // ── Autocomplete mode ──
      const notFoundLabel = isNotInListField ? "Not found? Click here" : "";
      const onNotFoundClick = isNotInListField
        ? () => handleNotFoundClick(name as keyof NotInListState)
        : () => {};

      // Per-field search handler
      let onInputChange: ((v: string) => void) | undefined;
      if (name === "university") onInputChange = handleUniversitySearch;
      else if (name === "institute") onInputChange = handleInstituteSearch;
      else if (name === "courseName") onInputChange = handleCourseSearch;

      return (
        <AutocompleteField
          name={name}
          label={field.label}
          options={getAutocompleteOptions(name)}
          value={formik.values[name]}
          error={isFieldError(name)}
          helperText={getFieldError(name)}
          size={fieldSize}
          loading={isFieldLoading(name)}
          onValueChange={handleAutocompleteChange}
          onBlur={handleAutocompleteBlur}
          onInputChange={onInputChange}
          notFoundLabel={notFoundLabel}
          onNotFoundClick={onNotFoundClick}
          displayValue={labelMap[name] || undefined}
          isUndecided={formik.values?.admissionStatus?.toLowerCase() === "undecided"}
        />
      );
    }

    return null;
  };

  // ── render ────────────────────────────────────────────────────────────────

  if (!fields || fields.length === 0) {
    return <Step2CourseDiscoverySkeleton />;
  }

  return (
    <>
      <Box sx={{ display: isMobile ? "column" : "flex", minWidth: 0, gap: 3 }}>
        <Box
          component="form"
          ref={formRef}
          sx={{ width: "100%" }}
          onSubmit={formik.handleSubmit}
          noValidate
        >
          <Box sx={{ mb: 4, bgcolor: isMobile ? "#EBF5F6" : "", p: { xs: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <img
                src={MultiStarIcon}
                style={{ width: "13px", height: "13px" }}
              />
              <Typography
                variant="body2"
                sx={{ color: "#251A00", fontWeight: 600 }}
              >
                Course Discovery
              </Typography>
            </Box>

            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "1.6rem", sm: "42px" },
                fontWeight: 700,
                lineHeight: 1.3,
                mb: 1,
              }}
            >
              Tell us about your{" "}
              <Box component="span" sx={{ color: "primary.main" }}>
                Dream University
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              We&apos;ll match your academic ambitions with the most competitive
              education loan rates available.
            </Typography>
          </Box>

          {/* ── Mobile Sidebar ── */}
          {isMobile && (
            <Sidebar
              currentStep={currentStep === 4 ? 3 : currentStep}
              completedSteps={completedSteps}
            />
          )}

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 3.5 },
              borderRadius: 2,
              border: "none",
              bgcolor: "#ffffff",
              boxShadow: isMobile ? "" : "0 2px 16px rgba(0,0,0,0.06)",
            }}
          >
            <Grid container spacing={2.5}>
              {visibleFields.map((field, idx: number) => (
                <Grid item xs={12} sm={6} key={field.id} sx={{ mb: { xs: 0, sm: "12px" } }}>
                  {renderField(field)}
                </Grid>
              ))}
            </Grid>

            {/* Actions */}
           {!isMobile && <Box sx={{ display: "flex", justifyContent: "end", mt: 3 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={onBack}
                  sx={{ flex: 1, py: 1.75, fontSize: "1rem", fontWeight: 600 }}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  disabled={isBtnLoading}
                  onClick={handleContinueClick}
                  sx={{
                    flex: 2,
                    py: 1.75,
                    fontSize: "1rem",
                    fontWeight: 700,
                  }}
                >
                  {isBtnLoading ? "Loading..." : "Continue"}
                </Button>
              </Box>
            </Box>}
          </Paper>
        </Box>

        {currentStep <= 3 && (
          <Box
            sx={{
              width: isMobile ? "100%" : 260,
              flexShrink: 0,
              bgcolor: "#EBF5F6",
              pl: isMobile ? 2 : 2,
              pr: isMobile ? 2 : 0,
              py: 3,
              position: "sticky",
              top: 64,
            }}
          >
            <RightPanel currentStep={currentStep} />
          </Box>
        )}
        {isMobile && (
          <StickyFooter progress={progress} onBack={onBack} onContinue={handleContinueClick} />
        )}
      </Box>
    </>
  );
};

export default Step2CourseDiscovery;
